// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { BatchRegistrationWasteUnit } from 'util/Types';
import { SelectField, SelectOption } from '../SelectField';
import { EmissionWasteUnitKind } from 'util/Enums';
import { Form } from 'react-final-form';
import styles from './BatchRegistrationFilter.module.scss';
import { useTranslation } from 'react-i18next';
import _snakeCase from 'lodash/snakeCase';

type BatchRegistrationFilterProps = {
  wasteUnits: BatchRegistrationWasteUnit[];
  categoryFilterDispatcher: [
    EmissionWasteUnitKind | undefined,
    Dispatch<SetStateAction<EmissionWasteUnitKind | undefined>>
  ];
  tenantFilterOptions: SelectOption[];
  tenantFilterDispatcher: [
    number | undefined,
    Dispatch<SetStateAction<number | undefined>>
  ];
};

const BatchRegistrationFilter: React.FC<BatchRegistrationFilterProps> = ({
  wasteUnits,
  categoryFilterDispatcher,
  tenantFilterOptions,
  tenantFilterDispatcher,
}) => {
  const [selectedKind, setSelectedKind] = categoryFilterDispatcher;
  const [selectedTenantId, setSelectedTenantId] = tenantFilterDispatcher;
  const { t } = useTranslation();

  const kindValues: EmissionWasteUnitKind[] = useMemo(() => {
    if (!wasteUnits.length || wasteUnits.every((unit) => !unit.kind)) {
      return [];
    }

    const orders = new Map(
      [
        EmissionWasteUnitKind.WASTE,
        EmissionWasteUnitKind.GENERAL,
        EmissionWasteUnitKind.WASTE_PAPER,
      ].map((k, i) => [k, i])
    );

    const kinds = Array.from(
      new Set(wasteUnits.map((unit) => unit.kind).filter(Boolean))
    ) as EmissionWasteUnitKind[];

    return kinds.sort((a, b) => {
      return (orders.get(a) || 0) - (orders.get(b) || 0);
    });
  }, [wasteUnits]);

  useEffect(() => {
    if (kindValues.length > 0) {
      setSelectedKind(kindValues[0]);
    } else {
      setSelectedKind(undefined);
    }
  }, [kindValues]);

  return (
    <div className={styles.FilterContainer}>
      {tenantFilterOptions.length > 0 && (
        <div className={styles.FilterItem}>
          <p>{t('batch_registration.filter_tenant')}</p>

          <Form onSubmit={() => {}}>
            {() => (
              <SelectField
                name="tenantFilter"
                placeholder={t('batch_registration.filter_tenant')}
                options={tenantFilterOptions}
                value={selectedTenantId?.toString()}
                onChange={(val) =>
                  setSelectedTenantId(val ? Number(val) : undefined)
                }
                isDarkmode
                wrapperClassName={styles.FilterSelect}
                allowClear
                searchable
                usePortal
              />
            )}
          </Form>
        </div>
      )}

      {kindValues.length > 0 && (
        <div className={styles.FilterItem}>
          <p>{t('batch_registration.filter_category')}</p>

          <Form onSubmit={() => {}}>
            {() => (
              <SelectField
                name="kindFilter"
                placeholder={t('batch_registration.filter_category')}
                options={kindValues.map((kind) => ({
                  label: t(`batch_registration.kind.${_snakeCase(kind)}`),
                  value: kind,
                }))}
                value={selectedKind}
                onChange={(val) =>
                  setSelectedKind(val as EmissionWasteUnitKind)
                }
                isDarkmode
                wrapperClassName={styles.FilterSelect}
                usePortal
              />
            )}
          </Form>
        </div>
      )}
    </div>
  );
};

export default BatchRegistrationFilter;
